import React, { useState, useEffect, useCallback } from 'react';
import AOHeader from '../../components/ao/AOHeader';
import LinkedInDataPanel from '../../components/ao/LinkedInDataPanel';

/**
 * Settings was one long scroll of fifteen sections mixing logins, how Auto
 * behaves, content tools and system checks. Split into four tabs on
 * 2026-10-19, the grouping Bart saw and liked. The tab lives in the address
 * (#connections and so on) so a link can open the right one.
 */
const SETTINGS_TABS = [
  { key: 'connections', label: 'Connections' },
  { key: 'auto', label: 'How Auto writes' },
  { key: 'content', label: 'Your content' },
  { key: 'system', label: 'System' },
];

function initialSettingsTab() {
  if (typeof window === 'undefined') return 'connections';
  const fromHash = window.location.hash.replace('#', '');
  if (SETTINGS_TABS.some((t) => t.key === fromHash)) return fromHash;
  return 'connections';
}

function StatusPill({ state }) {
  const s = state || 'not_connected';
  const map = {
    connected: { label: 'Connected', cls: 'bg-green-50 text-green-800 border-green-200' },
    not_connected: { label: 'Not connected', cls: 'bg-gray-50 text-gray-700 border-gray-200' },
    needs_reconnect: { label: 'Needs reconnect', cls: 'bg-yellow-50 text-yellow-900 border-yellow-200' },
  };
  const cfg = map[s] || map.not_connected;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function Settings() {
  const [email, setEmail] = useState('');
  const [tab, setTab] = useState(initialSettingsTab);

  function chooseTab(key) {
    setTab(key);
    try {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${key}`);
    } catch (_) {}
  }
  const [authChecked, setAuthChecked] = useState(false);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinMessage, setLinkedinMessage] = useState('');
  const [metaConnectStatus, setMetaConnectStatus] = useState(null);
  const [metaConnectMessage, setMetaConnectMessage] = useState('');
  const [xConnectStatus, setXConnectStatus] = useState(null);
  const [xConnectMessage, setXConnectMessage] = useState('');
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinState, setLinkedinState] = useState('not_connected'); // connected | not_connected | needs_reconnect
  const [linkedinReason, setLinkedinReason] = useState('');
  const [linkedinLoading, setLinkedinLoading] = useState(true);
  const [linkedinTestLoading, setLinkedinTestLoading] = useState(false);
  const [linkedinTestResult, setLinkedinTestResult] = useState(null); // 'success' | 'error' | null
  const [linkedinTestError, setLinkedinTestError] = useState('');
  const [linkedinPage, setLinkedinPage] = useState(null);
  const [linkedinPageCheck, setLinkedinPageCheck] = useState(null); // 'running' | 'passed' | error text
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [metaOverallState, setMetaOverallState] = useState('not_connected');
  const [metaTestLoading, setMetaTestLoading] = useState({ facebook: false, instagram: false });
  const [metaTestResult, setMetaTestResult] = useState({ facebook: null, instagram: null }); // success|error|null
  const [metaTestError, setMetaTestError] = useState({ facebook: '', instagram: '' });

  const [igPersonalLoading, setIgPersonalLoading] = useState(true);
  const [igPersonalStatus, setIgPersonalStatus] = useState(null);
  const [igPersonalError, setIgPersonalError] = useState('');
  const [igPersonalTestLoading, setIgPersonalTestLoading] = useState(false);
  const [igPersonalTestResult, setIgPersonalTestResult] = useState(null); // success|error|null
  const [igPersonalTestError, setIgPersonalTestError] = useState('');

  const [xLoading, setXLoading] = useState(true);
  const [xConnected, setXConnected] = useState(false);
  const [xState, setXState] = useState('not_connected'); // connected | not_connected | needs_reconnect
  const [xUsername, setXUsername] = useState('');
  const [xError, setXError] = useState('');
  const [xTestLoading, setXTestLoading] = useState(false);
  const [xTestResult, setXTestResult] = useState(null); // success|error|null
  const [xTestError, setXTestError] = useState('');

  const [dbCheckLoading, setDbCheckLoading] = useState(false);
  const [dbCheckError, setDbCheckError] = useState('');
  const [dbCheckResult, setDbCheckResult] = useState(null); // { missing: [], notes: [] }

  // Editorial memory loop (shared newsroom memory)
  const [editorialLoading, setEditorialLoading] = useState(false);
  const [editorialError, setEditorialError] = useState('');
  const [editorialMessage, setEditorialMessage] = useState('');
  const [editorialStats, setEditorialStats] = useState(null); // { total, counts }
  const [beatPrioritiesText, setBeatPrioritiesText] = useState('');
  const [editorialSettingsUpdatedAt, setEditorialSettingsUpdatedAt] = useState(null);

  // Corpus vector seeding
  const [corpusSeedLoading, setCorpusSeedLoading] = useState(false);
  const [corpusSeedError, setCorpusSeedError] = useState('');
  const [corpusSeedResult, setCorpusSeedResult] = useState(null); // { processed, succeeded, failed, total, final_count, message }

  // Reshare engine
  const [reshareLoading, setReshareLoading] = useState(false);
  const [reshareError, setReshareError] = useState('');
  const [reshareResult, setReshareResult] = useState(null);
  const [reshareAutoApprove, setReshareAutoApprove] = useState(false);
  const [reshareAutoApproveLoading, setReshareAutoApproveLoading] = useState(false);
  const [reshareSettingsLoading, setReshareSettingsLoading] = useState(true);
  const [igConnectOutcome, setIgConnectOutcome] = useState(null);
  const [pendingReshares, setPendingReshares] = useState([]);
  const [pendingResharesLoading, setPendingResharesLoading] = useState(true);
  const [reshareActionLoading, setReshareActionLoading] = useState({});
  const [reshareActionResult, setReshareActionResult] = useState({});
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(true);
  const [opportunitiesError, setOpportunitiesError] = useState('');
  const [opportunitiesSetupNeeded, setOpportunitiesSetupNeeded] = useState(false);

  const [brandLoading, setBrandLoading] = useState(true);
  const [brandAssets, setBrandAssets] = useState([]);
  const [brandError, setBrandError] = useState('');
  const [brandUploads, setBrandUploads] = useState([]); // [{ localId, file, label, variant, defaultLight, defaultDark }]
  const [brandUploading, setBrandUploading] = useState(false);
  const [brandUploadError, setBrandUploadError] = useState('');
  const [brandUploadMessage, setBrandUploadMessage] = useState('');

  const handleNavigate = useCallback((path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('provider') === 'linkedin_page') {
      setLinkedinPageCheck(params.get('status') === 'error' ? (params.get('message') || 'The connection failed.') : null);
    }
    if (params.get('provider') === 'linkedin') {
      setLinkedinStatus(params.get('status') || null);
      setLinkedinMessage(params.get('message') || '');
    }
    if (params.get('provider') === 'meta') {
      setMetaConnectStatus(params.get('status') || null);
      setMetaConnectMessage(params.get('message') || '');
    }
    if (params.get('provider') === 'x') {
      setXConnectStatus(params.get('status') || null);
      setXConnectMessage(params.get('message') || '');
    }

    // The Instagram sign in returns here with its own result on the address.
    const instagram = params.get('instagram');
    if (instagram === 'connected') {
      const username = params.get('username');
      const expires = params.get('expires');
      setIgConnectOutcome({
        ok: true,
        message: `Instagram connected${username ? ` as @${username}` : ''}${
          expires ? `. The connection runs to ${expires} and renews itself every Monday.` : '.'
        }`,
      });
    } else if (instagram === 'denied') {
      setIgConnectOutcome({ ok: false, message: 'Instagram sign in was cancelled, so nothing changed.' });
    } else if (instagram === 'error') {
      setIgConnectOutcome({
        ok: false,
        message: params.get('message') || 'Instagram could not be connected.',
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/ao/me');
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok) {
          window.location.replace('/ao/login');
          return;
        }
        if (!cancelled) {
          setEmail(json.email || '');
          setAuthChecked(true);
        }
      } catch (_) {
        window.location.replace('/ao/login');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    if (!email) {
      setLinkedinLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        fetch('/api/ao/linkedin/page-status')
          .then((r) => r.json())
          .then((json) => { if (!cancelled && json.ok) setLinkedinPage(json); })
          .catch(() => {});
        const res = await fetch(`/api/ao/linkedin/status`);
        if (cancelled) return;
        const json = await res.json().catch(() => ({}));
        if (json.ok) {
          setLinkedinConnected(!!json.connected);
          setLinkedinState(json.state || (json.connected ? 'connected' : 'not_connected'));
          setLinkedinReason(json.reason || '');
        }
      } catch (_) {}
      if (!cancelled) setLinkedinLoading(false);
    })();
    return () => { cancelled = true; };
  }, [authChecked, email]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      setMetaLoading(true);
      setMetaError('');
      try {
        const res = await fetch('/api/ao/meta/status');
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && json.ok) {
          setMetaStatus(json);
          setMetaOverallState(json.overallState || 'not_connected');
        } else {
          setMetaStatus(null);
          setMetaError(json.error || 'Meta status check failed');
        }
      } catch (e) {
        if (!cancelled) {
          setMetaStatus(null);
          setMetaError(e.message || 'Meta status check failed');
        }
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      setIgPersonalLoading(true);
      setIgPersonalError('');
      try {
        const res = await fetch('/api/ao/instagram-login/status');
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && json.ok) {
          setIgPersonalStatus(json);
        } else {
          setIgPersonalStatus(null);
          setIgPersonalError(json.error || 'Instagram Personal status check failed');
        }
      } catch (e) {
        if (!cancelled) {
          setIgPersonalStatus(null);
          setIgPersonalError(e.message || 'Instagram Personal status check failed');
        }
      } finally {
        if (!cancelled) setIgPersonalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      setXLoading(true);
      setXError('');
      try {
        const res = await fetch('/api/ao/x/status');
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && json.ok) {
          setXConnected(!!json.connected);
          setXUsername(json.username || '');
          setXState(json.state || (json.connected ? 'connected' : 'not_connected'));
        } else {
          setXConnected(false);
          setXUsername('');
          setXError(json.error || 'X status check failed');
        }
      } catch (e) {
        if (!cancelled) {
          setXConnected(false);
          setXUsername('');
          setXError(e.message || 'X status check failed');
        }
      } finally {
        if (!cancelled) setXLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  const maskedEmail = email ? `${email.slice(0, 2)}***@${email.split('@')[1] || '***'}` : '—';

  const runDbCheck = useCallback(async () => {
    if (!authChecked) return;
    setDbCheckLoading(true);
    setDbCheckError('');
    try {
      const res = await fetch('/api/ao/db-check');
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Setup check failed');
      setDbCheckResult({ missing: json.missing || [], notes: json.notes || [] });
    } catch (e) {
      setDbCheckResult(null);
      setDbCheckError(e.message || 'Setup check failed');
    } finally {
      setDbCheckLoading(false);
    }
  }, [authChecked]);

  const refreshEditorial = useCallback(async () => {
    if (!authChecked) return;
    setEditorialError('');
    try {
      const [settingsRes, statsRes] = await Promise.all([
        fetch('/api/ao/editorial/settings'),
        fetch('/api/ao/editorial/rebuild'),
      ]);

      const settingsJson = await settingsRes.json().catch(() => ({}));
      if (settingsRes.ok && settingsJson.ok) {
        const beat = Array.isArray(settingsJson.settings?.beat_priorities) ? settingsJson.settings.beat_priorities : [];
        setBeatPrioritiesText(beat.join('\n'));
        setEditorialSettingsUpdatedAt(settingsJson.settings?.updated_at || null);
      }

      const statsJson = await statsRes.json().catch(() => ({}));
      if (statsRes.ok && statsJson.ok) {
        setEditorialStats(statsJson.stats || null);
      }
    } catch (e) {
      setEditorialError(e.message || 'Could not load editorial settings');
    }
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    refreshEditorial();
  }, [authChecked, refreshEditorial]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      // Load reshare settings
      try {
        const res = await fetch('/api/ao/auto/reshare-settings');
        const json = await res.json().catch(() => ({}));
        if (!cancelled && json.ok) setReshareAutoApprove(!!json.auto_approve);
      } catch (_) {}
      if (!cancelled) setReshareSettingsLoading(false);

      // Load pending reshares
      try {
        const res = await fetch('/api/ao/auto/reshare-review');
        const json = await res.json().catch(() => ({}));
        if (!cancelled && json.ok) setPendingReshares(json.pending || []);
      } catch (_) {}
      if (!cancelled) setPendingResharesLoading(false);

      // Load new opportunities (read-only visibility)
      try {
        const res = await fetch('/api/ao/opportunities?status=new&limit=20');
        const json = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (json.ok) {
            setOpportunities(Array.isArray(json.opportunities) ? json.opportunities : []);
            setOpportunitiesSetupNeeded(false);
            setOpportunitiesError('');
          } else {
            const errMsg = String(json.error || '');
            if (/not set up yet|ao_opportunities/i.test(errMsg)) {
              setOpportunitiesSetupNeeded(true);
              setOpportunitiesError('');
            } else {
              setOpportunitiesError(errMsg || 'Could not load opportunities');
            }
            setOpportunities([]);
          }
        }
      } catch (_) {
        if (!cancelled) {
          setOpportunities([]);
          setOpportunitiesError('Could not load opportunities');
        }
      }
      if (!cancelled) setOpportunitiesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [authChecked]);

  const saveBeatPriorities = useCallback(async () => {
    if (!authChecked) return;
    setEditorialLoading(true);
    setEditorialError('');
    setEditorialMessage('');
    try {
      const beat = String(beatPrioritiesText || '')
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
      const res = await fetch('/api/ao/editorial/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ beat_priorities: beat }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Save failed');
      setEditorialMessage('Saved.');
      setEditorialSettingsUpdatedAt(json.settings?.updated_at || null);
    } catch (e) {
      setEditorialError(e.message || 'Save failed');
    } finally {
      setEditorialLoading(false);
    }
  }, [authChecked, beatPrioritiesText]);

  const rebuildEditorialMemoryNow = useCallback(async () => {
    if (!authChecked) return;
    if (!window.confirm('Rebuild editorial memory now? This can take a minute.')) return;
    setEditorialLoading(true);
    setEditorialError('');
    setEditorialMessage('');
    try {
      const res = await fetch('/api/ao/editorial/rebuild', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Rebuild failed');
      setEditorialMessage(`Rebuilt. Added ${json.totalInserted || 0} items.`);
      await refreshEditorial();
    } catch (e) {
      setEditorialError(e.message || 'Rebuild failed');
    } finally {
      setEditorialLoading(false);
    }
  }, [authChecked, refreshEditorial]);

  const handleSeedCorpus = useCallback(async () => {
    if (corpusSeedLoading) return;
    setCorpusSeedLoading(true);
    setCorpusSeedError('');
    setCorpusSeedResult(null);
    try {
      const res = await fetch('/api/ao/auto/seed-corpus', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        setCorpusSeedError(json.error || 'Seeding failed');
        return;
      }
      setCorpusSeedResult(json);
    } catch (e) {
      setCorpusSeedError(e.message || 'Could not reach the server');
    } finally {
      setCorpusSeedLoading(false);
    }
  }, [corpusSeedLoading]);

  const handleTriggerReshare = useCallback(async () => {
    if (reshareLoading) return;
    setReshareLoading(true);
    setReshareError('');
    setReshareResult(null);
    try {
      const res = await fetch('/api/ao/auto/reshare-journal', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        setReshareError(json.error || 'Reshare failed');
        return;
      }
      setReshareResult(json);
      // Refresh pending reshares
      const reviewRes = await fetch('/api/ao/auto/reshare-review');
      const reviewJson = await reviewRes.json().catch(() => ({}));
      if (reviewJson.ok) setPendingReshares(reviewJson.pending || []);
    } catch (e) {
      setReshareError(e.message || 'Could not reach the server');
    } finally {
      setReshareLoading(false);
    }
  }, [reshareLoading]);

  const handleReshareAutoApproveToggle = useCallback(async (value) => {
    setReshareAutoApproveLoading(true);
    try {
      const res = await fetch('/api/ao/auto/reshare-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_approve: value }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.ok) setReshareAutoApprove(value);
    } catch (_) {}
    setReshareAutoApproveLoading(false);
  }, []);

  const handleReshareAction = useCallback(async (slug, action) => {
    setReshareActionLoading((prev) => ({ ...prev, [slug]: action }));
    setReshareActionResult((prev) => ({ ...prev, [slug]: null }));
    try {
      const res = await fetch('/api/ao/auto/reshare-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, slug }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        setReshareActionResult((prev) => ({
          ...prev,
          [slug]: { ok: false, message: json.error || `${action} failed` },
        }));
        return;
      }
      setReshareActionResult((prev) => ({
        ...prev,
        [slug]: { ok: true, message: json.message },
      }));
      // Remove from pending list
      setPendingReshares((prev) => prev.filter((r) => r.slug !== slug));
    } catch (e) {
      setReshareActionResult((prev) => ({
        ...prev,
        [slug]: { ok: false, message: e.message || `${action} failed` },
      }));
    } finally {
      setReshareActionLoading((prev) => ({ ...prev, [slug]: null }));
    }
  }, []);

  const refreshBrandAssets = useCallback(async () => {
    if (!authChecked) return;
    setBrandLoading(true);
    setBrandError('');
    try {
      const res = await fetch('/api/ao/brand/assets');
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Could not load brand assets');
      setBrandAssets(Array.isArray(json.assets) ? json.assets : []);
    } catch (e) {
      setBrandAssets([]);
      setBrandError(e.message || 'Could not load brand assets');
    } finally {
      setBrandLoading(false);
    }
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    refreshBrandAssets();
  }, [authChecked, refreshBrandAssets]);

  const uploadBrandAssets = useCallback(async () => {
    if (!authChecked) return;
    if (brandUploading) return;
    setBrandUploadError('');
    setBrandUploadMessage('');

    const pending = Array.isArray(brandUploads) ? brandUploads : [];
    if (!pending.length) return;

    const missingLabel = pending.find((u) => !String(u?.label || '').trim());
    if (missingLabel) {
      setBrandUploadError('Each file needs a label before uploading.');
      return;
    }

    setBrandUploading(true);
    try {
      for (const u of pending) {
        const fd = new FormData();
        fd.append('label', String(u.label || '').trim());
        fd.append('variant', String(u.variant || 'other'));
        fd.append('defaultLight', u.defaultLight ? 'true' : 'false');
        fd.append('defaultDark', u.defaultDark ? 'true' : 'false');
        fd.append('file', u.file);

        const res = await fetch('/api/ao/brand/assets', { method: 'POST', body: fd });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok) throw new Error(json.error || 'Upload failed');
      }
      setBrandUploads([]);
      setBrandUploadMessage('Uploaded.');
      await refreshBrandAssets();
    } catch (e) {
      setBrandUploadError(e.message || 'Upload failed');
    } finally {
      setBrandUploading(false);
    }
  }, [authChecked, brandUploading, brandUploads, refreshBrandAssets]);

  const patchBrandAsset = useCallback(async (id, patch) => {
    if (!authChecked) return;
    try {
      const res = await fetch(`/api/ao/brand/assets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch || {}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Update failed');
      setBrandAssets((prev) => prev.map((a) => (a.id === id ? json.asset : a)));
    } catch (e) {
      setBrandUploadError(e.message || 'Update failed');
    }
  }, [authChecked]);

  const deleteBrandAsset = useCallback(async (id) => {
    if (!authChecked) return;
    if (!window.confirm('Delete this logo?')) return;
    try {
      const res = await fetch(`/api/ao/brand/assets/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Delete failed');
      setBrandAssets((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setBrandUploadError(e.message || 'Delete failed');
    }
  }, [authChecked]);

  async function handleLinkedInPageCheck() {
    setLinkedinPageCheck('running');
    try {
      const res = await fetch('/api/ao/linkedin/page-status', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      setLinkedinPageCheck(data.ok ? 'passed' : (data.error || 'The check failed.'));
    } catch (e) {
      setLinkedinPageCheck(e.message || 'The check failed.');
    }
  }

  async function handleLinkedInTestPost() {
    if (!authChecked) return;
    setLinkedinTestResult(null);
    setLinkedinTestError('');
    setLinkedinTestLoading(true);
    try {
      const res = await fetch('/api/providers/linkedin/test-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setLinkedinTestResult('success');
      } else {
        setLinkedinTestResult('error');
        setLinkedinTestError(data.error || 'Request failed');
      }
    } catch (e) {
      setLinkedinTestResult('error');
      setLinkedinTestError(e.message || 'Request failed');
    } finally {
      setLinkedinTestLoading(false);
    }
  }

  async function handleMetaTestPost(platform) {
    if (!authChecked) return;
    if (platform !== 'facebook' && platform !== 'instagram') return;
    setMetaTestResult((p) => ({ ...p, [platform]: null }));
    setMetaTestError((p) => ({ ...p, [platform]: '' }));
    setMetaTestLoading((p) => ({ ...p, [platform]: true }));
    try {
      const res = await fetch('/api/providers/meta/test-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.ok) {
        setMetaTestResult((p) => ({ ...p, [platform]: 'success' }));
      } else {
        setMetaTestResult((p) => ({ ...p, [platform]: 'error' }));
        setMetaTestError((p) => ({ ...p, [platform]: json.error || 'Request failed' }));
      }
    } catch (e) {
      setMetaTestResult((p) => ({ ...p, [platform]: 'error' }));
      setMetaTestError((p) => ({ ...p, [platform]: e.message || 'Request failed' }));
    } finally {
      setMetaTestLoading((p) => ({ ...p, [platform]: false }));
    }
  }

  async function handleIgPersonalTestPost() {
    if (!authChecked) return;
    setIgPersonalTestResult(null);
    setIgPersonalTestError('');
    setIgPersonalTestLoading(true);
    try {
      const res = await fetch('/api/providers/meta/test-post-personal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (json.ok) {
        setIgPersonalTestResult('success');
      } else {
        setIgPersonalTestResult('error');
        setIgPersonalTestError(json.error || 'Request failed');
      }
    } catch (e) {
      setIgPersonalTestResult('error');
      setIgPersonalTestError(e.message || 'Request failed');
    } finally {
      setIgPersonalTestLoading(false);
    }
  }

  async function handleXTestPost() {
    if (!authChecked) return;
    setXTestResult(null);
    setXTestError('');
    setXTestLoading(true);
    try {
      const res = await fetch('/api/providers/x/test-post', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (json.ok) {
        setXTestResult('success');
      } else {
        setXTestResult('error');
        setXTestError(json.error || 'Request failed');
      }
    } catch (e) {
      setXTestResult('error');
      setXTestError(e.message || 'Request failed');
    } finally {
      setXTestLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AOHeader active="settings" email={email} onNavigate={handleNavigate} />
      <main className="container mx-auto px-4 py-6 md:py-8 max-w-7xl pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md:pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600 mb-6">Owner console and configuration.</p>

        <nav aria-label="Settings sections" className="mb-8 -mx-4 px-4 overflow-x-auto">
          <div role="tablist" className="inline-flex gap-1 rounded-lg bg-gray-100 p-1 min-w-max">
            {SETTINGS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => chooseTab(t.key)}
                className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                  tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>

        {tab === 'system' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">System setup check</h2>
          <p className="text-sm text-gray-600 mb-4">
            If something throws a database error (like “missing column”), run this check to see which one-time setup steps are missing.
          </p>
          {dbCheckError ? (
            <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-800 text-sm">{dbCheckError}</div>
          ) : null}
          <button
            type="button"
            onClick={runDbCheck}
            disabled={dbCheckLoading}
            className="px-4 py-2 bg-gray-900 text-white font-semibold rounded hover:bg-gray-800 disabled:opacity-50"
          >
            {dbCheckLoading ? 'Checking…' : 'Run setup check'}
          </button>
          {dbCheckResult ? (
            <div className="mt-4">
              {Array.isArray(dbCheckResult.missing) && dbCheckResult.missing.length === 0 ? (
                <div className="p-3 rounded border border-green-200 bg-green-50 text-green-800 text-sm">
                  Looks good. No missing setup steps detected.
                </div>
              ) : (
                <>
                  <div className="p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm">
                    Missing setup steps detected. Run these files in Supabase SQL editor:
                  </div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {(dbCheckResult.missing || []).map((m, i) => (
                      <li key={i} className="border border-gray-200 rounded p-3 bg-white">
                        <div className="font-semibold text-gray-900">{m.sql}</div>
                        {m.reason ? <div className="text-gray-600 mt-1">{m.reason}</div> : null}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {Array.isArray(dbCheckResult.notes) && dbCheckResult.notes.length ? (
                <ul className="mt-3 text-xs text-gray-600 list-disc pl-5">
                  {dbCheckResult.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              ) : null}
            </div>
          ) : null}
        </section>
        )}

        {tab === 'auto' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Editorial memory loop</h2>
          <p className="text-sm text-gray-600 mb-4">
            This is the shared “newsroom memory” that helps Scout, Analyst, Studio, and Publisher avoid repeats and chase gaps.
          </p>

          {editorialError ? (
            <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-800 text-sm">{editorialError}</div>
          ) : null}
          {editorialMessage ? (
            <div className="mb-3 p-3 rounded border border-green-200 bg-green-50 text-green-800 text-sm">{editorialMessage}</div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <div className="text-sm font-semibold text-gray-900">Beat priorities (your universe)</div>
              <div className="text-xs text-gray-600 mt-1">One per line. Scout will use these as “what to chase next.”</div>
              <textarea
                value={beatPrioritiesText}
                onChange={(e) => setBeatPrioritiesText(e.target.value)}
                rows={8}
                className="mt-3 w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                placeholder="e.g.\nServant leadership under pressure\nUnaccountable leaders\nCulture drift\nLeadership courage"
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={saveBeatPriorities}
                  disabled={editorialLoading}
                  className="px-4 py-2 bg-gray-900 text-white font-semibold rounded hover:bg-gray-800 disabled:opacity-50"
                >
                  {editorialLoading ? 'Saving…' : 'Save priorities'}
                </button>
                <button
                  type="button"
                  onClick={rebuildEditorialMemoryNow}
                  disabled={editorialLoading}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-900 font-semibold rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  {editorialLoading ? 'Working…' : 'Rebuild editorial memory'}
                </button>
              </div>
              {editorialSettingsUpdatedAt ? (
                <div className="mt-2 text-xs text-gray-500">Last updated: {new Date(editorialSettingsUpdatedAt).toLocaleString()}</div>
              ) : null}
            </div>

            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <div className="text-sm font-semibold text-gray-900">Memory status</div>
              <div className="text-xs text-gray-600 mt-1">What the system currently has on record.</div>
              <div className="mt-3 text-sm text-gray-800">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total items</span>
                  <span className="font-semibold">{editorialStats?.total ?? '—'}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {editorialStats?.counts ? Object.entries(editorialStats.counts).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-gray-600">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  )) : (
                    <div className="text-gray-500">—</div>
                  )}
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Tip: if this shows empty, run “System setup check” above to see which one-time database files are missing.
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ─── Corpus library ─────────────────────────────────────────── */}
        {tab === 'content' && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Corpus library</h2>
              <p className="mt-1 text-sm text-gray-500">
                The corpus library powers Auto&apos;s semantic search across all your published writing.
                Run this once to fill the library with all existing documents. After that, new posts embed automatically on publish.
              </p>
            </div>
          </div>

          {corpusSeedResult && (
            <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              corpusSeedResult.failed === 0
                ? 'border-green-200 bg-green-50 text-green-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}>
              <p className="font-medium">{corpusSeedResult.message}</p>
              <p className="mt-1 text-xs opacity-75">
                {corpusSeedResult.final_count} documents now in the corpus library.
                {corpusSeedResult.failed > 0 && ' Run again to retry failed documents.'}
              </p>
            </div>
          )}

          {corpusSeedError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {corpusSeedError}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSeedCorpus}
              disabled={corpusSeedLoading}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {corpusSeedLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin text-gray-500" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Filling corpus library…
                </>
              ) : (
                'Fill corpus library'
              )}
            </button>
            <p className="text-xs text-gray-400">
              Takes 1–2 minutes for a full corpus. Safe to run multiple times.
            </p>
          </div>
        </section>
        )}

        {/* ─── Reshare engine ─────────────────────────────────────────── */}
        {tab === 'auto' && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Content reshare engine</h2>
              <p className="mt-1 text-sm text-gray-500">
                Resurfaces one existing journal entry per week across all automated channels with fresh captions.
                The engine selects based on your content ecosystem and current leadership conversations.
                Review and approve before posts go live, or turn on auto-approve once you trust it.
              </p>
            </div>
          </div>

          {/* Auto-approve toggle */}
          <div className="mb-5 flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-900">Auto-approve reshares</p>
              <p className="text-xs text-gray-500 mt-0.5">
                When on, reshares schedule automatically without review. Turn on once the engine has proven it makes good decisions.
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleReshareAutoApproveToggle(!reshareAutoApprove)}
              disabled={reshareAutoApproveLoading || reshareSettingsLoading}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                reshareAutoApprove ? 'bg-gray-900' : 'bg-gray-200'
              }`}
              aria-pressed={reshareAutoApprove}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  reshareAutoApprove ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Manual trigger */}
          {reshareResult && (
            <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              reshareResult.pending_review
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-green-200 bg-green-50 text-green-900'
            }`}>
              <p className="font-medium">{reshareResult.message}</p>
              {reshareResult.pending_review && (
                <p className="mt-1 text-xs opacity-75">Scroll down to review and approve the selected entry.</p>
              )}
              {!reshareResult.pending_review && reshareResult.schedule_date && (
                <p className="mt-1 text-xs opacity-75">
                  Selected: <span className="font-medium">{reshareResult.title}</span> — {reshareResult.selection_reason}
                </p>
              )}
            </div>
          )}

          {reshareError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {reshareError}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleTriggerReshare}
              disabled={reshareLoading}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {reshareLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin text-gray-500" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Running reshare selection…
                </>
              ) : (
                'Trigger reshare now'
              )}
            </button>
            <p className="text-xs text-gray-400">
              Runs the intelligent selection and generates fresh captions for one entry.
            </p>
          </div>

          {/* The stack itself lives on the Queue. These are the engine's switches. */}
          {pendingReshares.length > 0 ? (
            <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">
                {pendingReshares.length} reshare{pendingReshares.length === 1 ? '' : 's'} waiting for you
              </p>
              <p className="text-xs text-gray-600 mt-1">
                They used to sit on this page, where they went unseen for months. Read and approve them on the Queue.
              </p>
              <button
                type="button"
                onClick={() => handleNavigate('/ao/analyst?queue=1')}
                className="mt-3 px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-700"
              >
                Open the Queue
              </button>
            </div>
          ) : (
            <p className="mt-5 text-sm text-gray-400">No reshares pending review.</p>
          )}
        </section>
        )}

        {tab === 'auto' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Opportunities</h2>
          <p className="text-sm text-gray-600 mb-4">
            Strong external signal caught during reshare research (named reports, real stats, clear events) lands here so it is not lost inside a single caption.
          </p>

          {opportunitiesLoading ? (
            <p className="text-sm text-gray-400">Loading opportunities…</p>
          ) : opportunitiesSetupNeeded ? (
            <p className="text-sm text-gray-500">
              Opportunities are not set up yet — run <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">database/ao_opportunities.sql</code> in Supabase.
            </p>
          ) : opportunitiesError ? (
            <div className="p-3 rounded border border-red-200 bg-red-50 text-red-800 text-sm">{opportunitiesError}</div>
          ) : opportunities.length > 0 ? (
            <div className="space-y-4">
              {opportunities.map((opp) => (
                <div key={opp.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-900">{opp.title || 'Untitled opportunity'}</p>
                    {opp.created_at ? (
                      <p className="text-xs text-gray-400 mt-1">{new Date(opp.created_at).toLocaleString()}</p>
                    ) : null}
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {opp.opportunity_brief ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Brief</p>
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{opp.opportunity_brief}</p>
                      </div>
                    ) : null}
                    {opp.why_it_matters ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Why it matters</p>
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{opp.why_it_matters}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No new opportunities yet.</p>
          )}
        </section>
        )}

        {tab === 'content' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Brand assets</h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload AO logos once (SVG, PNG, or JPEG) and label them so Studio can use the right one when generating branded graphics.
          </p>

          {brandError ? (
            <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-800 text-sm">{brandError}</div>
          ) : null}
          {brandUploadError ? (
            <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-800 text-sm">{brandUploadError}</div>
          ) : null}
          {brandUploadMessage ? (
            <div className="mb-3 p-3 rounded border border-green-200 bg-green-50 text-green-800 text-sm">{brandUploadMessage}</div>
          ) : null}

          <div className="border border-gray-200 rounded p-4 bg-gray-50">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">Upload logos</div>
              <div className="text-xs text-gray-500">SVG, PNG, or JPEG</div>
            </div>
            <div className="mt-3">
              <input
                type="file"
                accept=".svg,.png,image/svg+xml,image/png"
                multiple
                onChange={(e) => {
                  setBrandUploadError('');
                  setBrandUploadMessage('');
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  setBrandUploads((prev) => ([
                    ...(Array.isArray(prev) ? prev : []),
                    ...files.map((f) => ({
                      localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                      file: f,
                      label: '',
                      variant: 'other',
                      defaultLight: false,
                      defaultDark: false,
                    })),
                  ]));
                  e.target.value = '';
                }}
                className="block w-full text-sm"
              />
            </div>

            {Array.isArray(brandUploads) && brandUploads.length ? (
              <div className="mt-4 space-y-3">
                {brandUploads.map((u) => (
                  <div key={u.localId} className="border border-gray-200 rounded bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-sm text-gray-900">
                        <span className="font-semibold">File:</span> {u.file?.name || 'logo'}
                      </div>
                      <button
                        type="button"
                        onClick={() => setBrandUploads((prev) => (prev || []).filter((x) => x.localId !== u.localId))}
                        className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Label (required)</label>
                        <input
                          value={u.label}
                          onChange={(e) => setBrandUploads((prev) => (prev || []).map((x) => (x.localId === u.localId ? { ...x, label: e.target.value } : x)))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder="e.g., AO mark (white), Wordmark (black), Lockup dark…"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Variant</label>
                        <select
                          value={u.variant || 'other'}
                          onChange={(e) => setBrandUploads((prev) => (prev || []).map((x) => (x.localId === u.localId ? { ...x, variant: e.target.value } : x)))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                        >
                          <option value="mark">mark</option>
                          <option value="wordmark">wordmark</option>
                          <option value="lockup_light">lockup_light</option>
                          <option value="lockup_dark">lockup_dark</option>
                          <option value="other">other</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!u.defaultLight}
                          onChange={(e) => setBrandUploads((prev) => (prev || []).map((x) => (x.localId === u.localId ? { ...x, defaultLight: e.target.checked } : x)))}
                        />
                        Default for light backgrounds
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!u.defaultDark}
                          onChange={(e) => setBrandUploads((prev) => (prev || []).map((x) => (x.localId === u.localId ? { ...x, defaultDark: e.target.checked } : x)))}
                        />
                        Default for dark backgrounds
                      </label>
                    </div>
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={uploadBrandAssets}
                    disabled={brandUploading}
                    className="px-4 py-2 bg-gray-900 text-white font-semibold rounded hover:bg-gray-800 disabled:opacity-50"
                  >
                    {brandUploading ? 'Uploading…' : 'Upload'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBrandUploads([])}
                    disabled={brandUploading}
                    className="px-4 py-2 border border-gray-300 bg-white text-gray-700 font-semibold rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500">Labels are required so you can reference them later in Studio.</span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-5">
            <div className="text-sm font-semibold text-gray-900 mb-2">Saved logos</div>
            {brandLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : Array.isArray(brandAssets) && brandAssets.length ? (
              <ul className="space-y-3">
                {brandAssets.slice(0, 50).map((a) => (
                  <li key={a.id} className="border border-gray-200 rounded p-3 bg-white">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{a.label}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {a.variant || 'other'}
                          {a.is_default_light ? ' · default light' : ''}
                          {a.is_default_dark ? ' · default dark' : ''}
                        </div>
                        {a.public_url ? (
                          <a className="text-xs text-blue-700 hover:underline break-all" href={a.public_url} target="_blank" rel="noreferrer">
                            Preview file
                          </a>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => patchBrandAsset(a.id, { is_default_light: true })}
                          className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                        >
                          Set default light
                        </button>
                        <button
                          type="button"
                          onClick={() => patchBrandAsset(a.id, { is_default_dark: true })}
                          className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                        >
                          Set default dark
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteBrandAsset(a.id)}
                          className="px-2.5 py-1 text-xs rounded border border-red-200 bg-red-50 text-red-800 hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No logos uploaded yet.</p>
            )}
          </div>
        </section>
        )}

        {tab === 'connections' && linkedinStatus === 'connected' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">LinkedIn connected. You can use LinkedIn for publishing.</div>
        )}
        {tab === 'connections' && linkedinStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">LinkedIn connection failed.{linkedinMessage ? ` ${linkedinMessage}` : ''}</div>
        )}
        {tab === 'connections' && metaConnectStatus === 'connected' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">Meta connected. You can publish to Facebook and Instagram.</div>
        )}
        {tab === 'connections' && metaConnectStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">Meta connection failed.{metaConnectMessage ? ` ${metaConnectMessage}` : ''}</div>
        )}
        {tab === 'connections' && xConnectStatus === 'connected' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">X connected. You can publish to @archetypeog.</div>
        )}
        {tab === 'connections' && xConnectStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">X connection failed.{xConnectMessage ? ` ${xConnectMessage}` : ''}</div>
        )}

        {tab === 'connections' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">LinkedIn</h2>
          <p className="text-gray-600 text-sm mb-4">Connect your personal LinkedIn account for AO publishing. Single-owner; tokens are stored server-side only.</p>
          {linkedinLoading ? (
            <p className="text-gray-500 text-sm">Checking connection…</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill state={linkedinState} />
                <a
                  href="/api/auth/linkedin/start"
                  className="inline-block px-4 py-2 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0A66C2]"
                  style={linkedinConnected ? { border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151' } : { backgroundColor: '#0A66C2', color: '#fff' }}
                >
                  {(linkedinState === 'connected' || linkedinState === 'needs_reconnect') ? 'Reconnect' : 'Connect LinkedIn'}
                </a>
                {linkedinConnected && (
                  <button
                    type="button"
                    onClick={handleLinkedInTestPost}
                    disabled={linkedinTestLoading}
                    className="inline-block px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0A66C2] disabled:opacity-50"
                  >
                    {linkedinTestLoading ? 'Posting…' : 'Post LinkedIn Test'}
                  </button>
                )}
              </div>
              {linkedinState === 'needs_reconnect' && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                  LinkedIn needs reconnect.{linkedinReason ? ` ${linkedinReason}` : ''}
                </div>
              )}
              {linkedinTestResult === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">LinkedIn test post published.</div>
              )}
              {linkedinTestResult === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">LinkedIn test post failed.{linkedinTestError ? ` ${linkedinTestError}` : ''}</div>
              )}
            </div>
          )}
          {!linkedinLoading && (
            <LinkedInDataPanel
              connected={linkedinConnected}
              onDisconnected={() => {
                setLinkedinConnected(false);
                setLinkedinState('not_connected');
                setLinkedinReason('');
              }}
            />
          )}
        </section>
        )}

        {tab === 'connections' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">LinkedIn company page</h2>
          <p className="text-gray-600 text-sm mb-4">
            Connects the Archetype Original page through the AO Page Publisher app. It does not change your personal
            LinkedIn connection above.
          </p>
          {!linkedinPage ? (
            <p className="text-gray-500 text-sm">Checking connection…</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill state={linkedinPage.state} />
                {linkedinPage.app_ready ? (
                  <a
                    href="/api/auth/linkedin/page-start"
                    className="inline-block px-4 py-2 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0A66C2]"
                    style={linkedinPage.connected ? { border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151' } : { backgroundColor: '#0A66C2', color: '#fff' }}
                  >
                    {linkedinPage.state === 'not_connected' ? 'Connect company page' : 'Reconnect'}
                  </a>
                ) : (
                  <span className="text-sm text-gray-600">Waiting on the app keys to be added to the site.</span>
                )}
                {linkedinPage.connected && (
                  <button
                    type="button"
                    onClick={handleLinkedInPageCheck}
                    disabled={linkedinPageCheck === 'running'}
                    className="px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                  >
                    {linkedinPageCheck === 'running' ? 'Checking…' : 'Check it can post (nothing appears publicly)'}
                  </button>
                )}
              </div>
              {linkedinPageCheck === 'passed' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">The company page can post. The test draft was removed and nothing appeared publicly.</div>
              )}
              {linkedinPageCheck && linkedinPageCheck !== 'passed' && linkedinPageCheck !== 'running' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{linkedinPageCheck}</div>
              )}
            </div>
          )}
        </section>
        )}

        {tab === 'connections' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Meta (Facebook + Instagram)</h2>
          <p className="text-gray-600 text-sm mb-4">
            Connecting here covers two channels at once: your Facebook Page and the Instagram Business account linked to
            it. Your personal Instagram is just below and connects on its own, because Meta only reaches an Instagram
            account through this door when it is attached to a Page, and @mediaphish is not.
          </p>
          {metaLoading ? (
            <p className="text-gray-500 text-sm">Checking connection…</p>
          ) : metaError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">Meta status check failed. {metaError}</div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill state={metaOverallState} />
                <a
                  href="/api/auth/meta/start"
                  className="inline-block px-4 py-2 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-1"
                  style={metaStatus?.facebook?.connected || metaStatus?.instagram?.connected ? { border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151' } : { backgroundColor: '#1877F2', color: '#fff' }}
                >
                  {(metaStatus?.facebook?.connected || metaStatus?.instagram?.connected) ? 'Reconnect Meta' : 'Connect Meta'}
                </a>
                {metaStatus?.source === 'stored' && (
                  <span className="text-xs text-gray-500">Connected via AO Settings</span>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="p-3 border border-gray-200 rounded">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Facebook Page</span>
                    <StatusPill state={metaStatus?.facebook?.state} />
                  </div>
                  {metaStatus?.facebook?.connected ? (
                    <p className="text-xs text-gray-500 mt-1">{metaStatus.facebook.page?.name || 'Page'}{metaStatus.facebook.page?.id ? ` · ${metaStatus.facebook.page.id}` : ''}</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">{metaStatus?.facebook?.reason || 'Missing configuration'}</p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMetaTestPost('facebook')}
                      disabled={!metaStatus?.facebook?.connected || metaTestLoading.facebook}
                      className="inline-block px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {metaTestLoading.facebook ? 'Posting…' : 'Post Facebook Test'}
                    </button>
                  </div>
                  {metaTestResult.facebook === 'success' && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm">Facebook test post published.</div>
                  )}
                  {metaTestResult.facebook === 'error' && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">Facebook test post failed.{metaTestError.facebook ? ` ${metaTestError.facebook}` : ''}</div>
                  )}
                </div>

                <div className="p-3 border border-gray-200 rounded">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">Instagram Business</span>
                    <StatusPill state={metaStatus?.instagram?.state} />
                  </div>
                  {metaStatus?.instagram?.connected ? (
                    <p className="text-xs text-gray-500 mt-1">@{metaStatus.instagram.account?.username || 'connected'}{metaStatus.instagram.account?.id ? ` · ${metaStatus.instagram.account.id}` : ''}</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">{metaStatus?.instagram?.reason || 'Missing configuration'}</p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMetaTestPost('instagram')}
                      disabled={!metaStatus?.instagram?.connected || metaTestLoading.instagram}
                      className="inline-block px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {metaTestLoading.instagram ? 'Posting…' : 'Post Instagram Test'}
                    </button>
                  </div>
                  {metaTestResult.instagram === 'success' && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm">Instagram test post published.</div>
                  )}
                  {metaTestResult.instagram === 'error' && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">Instagram test post failed.{metaTestError.instagram ? ` ${metaTestError.instagram}` : ''}</div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Test posts publish real content to your Facebook Page and Instagram Business feed. If connection is missing, see <a href="https://github.com/mediaphish/archetype-original/blob/main/notes/SOCIAL_VERCEL_ENV.md" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">notes/SOCIAL_VERCEL_ENV.md</a>.
              </p>
            </div>
          )}
        </section>
        )}

        {tab === 'connections' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Instagram Personal</h2>
          <p className="text-gray-600 text-sm mb-4">
            Your personal Instagram account (@mediaphish). It signs in separately from the Facebook Page and Instagram
            Business above, because it is not attached to a Page and Meta will not reach it through that connection.
            Once connected it renews itself every Monday.
          </p>
          {igPersonalLoading ? (
            <p className="text-gray-500 text-sm">Checking connection…</p>
          ) : igPersonalError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">Instagram Personal status check failed. {igPersonalError}</div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 border border-gray-200 rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Instagram Personal</span>
                  <StatusPill state={igPersonalStatus?.state || (igPersonalStatus?.connected ? 'connected' : 'not_connected')} />
                </div>
                {igPersonalStatus?.connected ? (
                  <p className="text-xs text-gray-500 mt-1">
                    @{igPersonalStatus.username || 'mediaphish'}
                    {igPersonalStatus.instagram_user_id ? ` · ${igPersonalStatus.instagram_user_id}` : ''}
                    {igPersonalStatus.expires_at
                      ? ` · token expires ${new Date(igPersonalStatus.expires_at).toLocaleDateString()}`
                      : ''}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Not connected — no personal Instagram token on file.</p>
                )}
                {igPersonalStatus?.expiry_warning && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                    {igPersonalStatus.expiry_warning}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a
                    href="/api/ao/instagram-login/start"
                    className="inline-block px-4 py-2 text-sm font-semibold rounded bg-gray-900 text-white hover:bg-gray-700"
                  >
                    {igPersonalStatus?.connected ? 'Reconnect Instagram' : 'Connect Instagram'}
                  </a>
                  <button
                    type="button"
                    onClick={handleIgPersonalTestPost}
                    disabled={!igPersonalStatus?.connected || igPersonalTestLoading}
                    className="inline-block px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {igPersonalTestLoading ? 'Posting…' : 'Post Instagram Personal Test'}
                  </button>
                </div>
                {/* The sign in ends by sending him back here with the outcome on the address. */}
                {igConnectOutcome ? (
                  <div
                    className={`mt-3 p-2 rounded text-sm border ${
                      igConnectOutcome.ok
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                  >
                    {igConnectOutcome.message}
                  </div>
                ) : null}
                {igPersonalTestResult === 'success' && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
                    Instagram Personal test post published to @mediaphish.
                  </div>
                )}
                {igPersonalTestResult === 'error' && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
                    Instagram Personal test post failed.{igPersonalTestError ? ` ${igPersonalTestError}` : ''}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                This posts a real test image and caption to your personal Instagram feed. It does not use the Facebook Page / Instagram Business connection above.
              </p>
            </div>
          )}
        </section>
        )}

        {tab === 'connections' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">X</h2>
          <p className="text-gray-600 text-sm mb-4">Connect your X account for publishing.</p>

          {xLoading ? (
            <p className="text-gray-500 text-sm">Checking connection…</p>
          ) : xError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">X status check failed. {xError}</div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill state={xState} />
                {xState === 'connected' && xUsername ? (
                  <span className="text-sm text-gray-600">@{xUsername}</span>
                ) : null}
                <a
                  href="/api/auth/x/start"
                  className="inline-block px-4 py-2 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-1"
                  style={xConnected ? { border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151' } : { backgroundColor: '#111827', color: '#fff' }}
                >
                  {(xState === 'connected' || xState === 'needs_reconnect') ? 'Reconnect X' : 'Connect X'}
                </a>
                {xConnected && (
                  <button
                    type="button"
                    onClick={handleXTestPost}
                    disabled={xTestLoading}
                    className="inline-block px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {xTestLoading ? 'Posting…' : 'Post X Test'}
                  </button>
                )}
              </div>
              {xState === 'needs_reconnect' && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                  X needs reconnect.
                </div>
              )}
              {xTestResult === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">X test post published.</div>
              )}
              {xTestResult === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">X test post failed.{xTestError ? ` ${xTestError}` : ''}</div>
              )}
            </div>
          )}
        </section>
        )}

        {tab === 'system' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner</h2>
          <p className="text-gray-600">Allowed email: <span className="font-mono">{maskedEmail}</span></p>
          <p className="text-sm text-gray-500 mt-2">Single-owner console. Only the configured owner can sign in via magic link.</p>
        </section>
        )}

        {tab === 'auto' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Scout sources</h2>
          <p className="text-gray-600 text-sm">
            Sources and scan inputs live in Scout now. This keeps “Settings” focused on connections and security.
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => handleNavigate('/ao/scout')}
              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
            >
              Go to Scout
            </button>
          </div>
        </section>
        )}

        {tab === 'content' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Publisher defaults</h2>
          <p className="text-gray-500 text-sm">Default platforms and schedule time can be set here. See <a href="https://github.com/mediaphish/archetype-original/blob/main/notes/SOCIAL_VERCEL_ENV.md" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">notes/SOCIAL_VERCEL_ENV.md</a> for environment variables.</p>
        </section>
        )}

        {tab === 'system' && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>
          <p className="text-gray-500 text-sm">Magic links expire after 15 minutes. There is no public signup; only the owner email can request a link.</p>
        </section>
        )}
      </main>
    </div>
  );
}
